import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { ChevronDown } from 'lucide-react-native';
import { useCallback, useMemo } from 'react';

import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { getOpenInquiriesV2QueryOptions, type InquiryV2 } from '@/services/getInquiriesV2';
import { userIdAtom } from '@/services/globalState';
import { type InquiryLabel } from '@/services/inquiryLabels';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';

type InquirySelectorProps = {
  selectedConversationId: string | null;
  onSelect: (conversationId: string, inquiryUserId: number) => void;
  isDisabled?: boolean;
};

type InquiryOption = {
  conversationId: string;
  inquiryUserId: number;
  displayLabel: string;
};

function getLabelValue(labels: InquiryLabel[] | undefined, key: string): string | null {
  if (!labels) {
    return null;
  }

  const label = labels.find((l) => l.key.toLowerCase() === key);

  if (!label) {
    return null;
  }

  if (typeof label.value === 'string') {
    return label.value;
  }

  return null;
}

function formatInquiryOption(inquiry: InquiryV2): string {
  const parts: string[] = [];

  if (inquiry.recipient) {
    const { name } = getProfileNameAndInitials({
      firstName: inquiry.recipient.firstName,
      lastName: inquiry.recipient.lastName,
      identifiesAsDoctor: inquiry.recipient.identifiesAsDoctor,
    });

    parts.push(name);
  }

  if (inquiry.title?.cancerFocus) {
    parts.push(inquiry.title.cancerFocus);
  }

  const labelParts: string[] = [];

  const age = getLabelValue(inquiry.labels, 'age');

  const gender = getLabelValue(inquiry.labels, 'gender');

  if (age) {
    labelParts.push(age);
  }

  if (gender) {
    labelParts.push(gender);
  }

  if (labelParts.length > 0) {
    parts.push(labelParts.join(', '));
  }

  return parts.join(' • ');
}

export function InquirySelector({
  selectedConversationId,
  onSelect,
  isDisabled,
}: InquirySelectorProps) {
  const userId = useAtomValue(userIdAtom);

  const { data: inquiriesData, isLoading } = useInfiniteQuery({
    ...getOpenInquiriesV2QueryOptions(userId ?? undefined),
    enabled: !!userId,
  });

  const inquiryOptions = useMemo((): InquiryOption[] => {
    if (!inquiriesData?.pages || !userId) {
      return [];
    }

    const allInquiries = inquiriesData.pages.flatMap((page) => page?.inquiries ?? []);

    return allInquiries
      .filter((inquiry) => String(inquiry.author?.id) === userId)
      .map((inquiry) => ({
        conversationId: inquiry.conversationId,
        inquiryUserId: inquiry.author?.id ?? 0,
        displayLabel: formatInquiryOption(inquiry),
      }));
  }, [inquiriesData, userId]);

  const selectedOption = useMemo(
    () => inquiryOptions.find((opt) => opt.conversationId === selectedConversationId),
    [inquiryOptions, selectedConversationId]
  );

  const handleValueChange = useCallback(
    (value: string) => {
      if (!value || value === selectedConversationId) {
        return;
      }

      const option = inquiryOptions.find((opt) => opt.conversationId === value);

      if (option) {
        onSelect(option.conversationId, option.inquiryUserId);
      }
    },
    [selectedConversationId, inquiryOptions, onSelect]
  );

  if (inquiryOptions.length === 0) {
    return null;
  }

  return (
    <Select
      selectedValue={selectedConversationId ?? ''}
      onValueChange={handleValueChange}
      isDisabled={isDisabled || isLoading}>
      <SelectTrigger size="sm">
        <SelectInput
          placeholder={isLoading ? 'Loading inquiries...' : 'Select an inquiry'}
          value={isLoading ? 'Loading...' : (selectedOption?.displayLabel ?? '')}
        />
        <SelectIcon as={ChevronDown} className="mr-2" />
      </SelectTrigger>

      <SelectPortal>
        <SelectBackdrop />
        <SelectContent className="max-h-[70vh]">
          <SelectDragIndicatorWrapper>
            <SelectDragIndicator />
          </SelectDragIndicatorWrapper>
          <SelectScrollView>
            {inquiryOptions.map((option) => (
              <SelectItem
                key={option.conversationId}
                label={option.displayLabel}
                value={option.conversationId}
              />
            ))}
          </SelectScrollView>
        </SelectContent>
      </SelectPortal>
    </Select>
  );
}
